import {useEffect, useState, useCallback} from "react";
import {NewSmallUserModel, UserRequests} from "@/app/api/user";
import {CourtBigModel, CourtModel, CourtRequestModel, CourtsRequestModel, CourtsRequests} from "@/app/api/courts";
import {ReviewModel, ReviewsRequests} from "@/app/api/reviews";
import { logEventSafe } from "@/app/utils/analytics";

export interface ScreenState {
  hasError?: string;
  courts?: CourtModel[];
  court?: CourtBigModel
  reviews?: ReviewModel[]
  user?: NewSmallUserModel
  courtLoading: boolean
  coords?: { lat: number; lng: number }
  currentPage: number;
  pageSize: number;
  hasMorePages: boolean;
  isLoadingMore: boolean;
}

const useMap = () => {
  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    courts: undefined,
    court: undefined,
    reviews: undefined,
    user: undefined,
    courtLoading: false,
    coords: undefined,
    currentPage: 0,
    pageSize: 20, // Default page size
    hasMorePages: true,
    isLoadingMore: false
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  useEffect(() => {
    try {
      const PAGE_SIZE = 20;
      const data: CourtsRequestModel = {
        page: 0,
      }
      CourtsRequests.getCourts(data).then((res) => {
        updateState({
          courts: res,
          currentPage: 0,
          hasMorePages: res.length === PAGE_SIZE
        });
      });
      UserRequests.getCurrentUser().then((res) => {
        updateState({user: res.user});
      });
    } catch (e) {
      console.error(e);
    }
  }, [updateState]);

  const loadMoreCourts = useCallback(async (filters: CourtsRequestModel = {}): Promise<CourtModel[]> => {
    if (state.isLoadingMore || !state.hasMorePages) return [];
    
    updateState({ isLoadingMore: true });
    
    try {
      const data: CourtsRequestModel = {
        ...filters,
        page: state.currentPage + 1,
      };
      
      const newCourts = await CourtsRequests.getCourts(data);
      const allCourts = [...(state.courts || []), ...newCourts];
      
      updateState({
        courts: allCourts,
        currentPage: state.currentPage + 1,
        hasMorePages: newCourts.length === state.pageSize,
        isLoadingMore: false
      });
      
      return newCourts;
    } catch (e) {
      console.error(e);
      updateState({ isLoadingMore: false });
      return [];
    }
  }, [state.currentPage, state.hasMorePages, state.isLoadingMore, state.courts, state.pageSize, updateState]);

  const uploadSelectedCourt = useCallback((courtId?: number | undefined) => {
    if (!courtId) {
      updateState({court: undefined, reviews: undefined});
      return;
    }

    updateState({courtLoading: true});

    try {
      const data: CourtRequestModel = {
        courtId: courtId
      }
      CourtsRequests.getCourt(data).then((res) => {
        updateState({court: res, courtLoading: false});
        // Log club view
        if (courtId) {
          logEventSafe('padel_club_view', { club_id: courtId });
        }
      });
      ReviewsRequests.getCourtReview(data).then((res) => {
        updateState({reviews: res});
      });
    } catch (e) {
      console.error(e);
      updateState({courtLoading: false});
    }
  }, [updateState]);

  const applyFilters = useCallback((filters: CourtsRequestModel) => {
    const data: CourtsRequestModel = {...filters, page: 0}
    CourtsRequests.getCourts(data).then((res) => {
      updateState({
        courts: res,
        currentPage: 0,
        hasMorePages: res.length === state.pageSize
      });
      // Log court search when filters applied
      logEventSafe('padel_court_search', {
        search_location: state.user ? `${state.user.lastLat},${state.user.lastLng}` : undefined,
        filters_used: Object.keys(filters).filter((k) => (filters as any)[k] !== undefined).join(',') || undefined
      });
    });
  }, [updateState, state.pageSize]);

  const setCoords = useCallback((coords: { lat: number; lng: number }) => {
    updateState({coords});
  }, [updateState]);

  return {
    state,
    uploadSelectedCourt,
    applyFilters,
    setCoords,
    loadMoreCourts
  };
};

export default useMap;